import {Box, Button, ButtonGroup, Grid, GridItem, IconButton, Input, InputGroup, InputRightElement, Menu, MenuButton, MenuItem, MenuList, Text} from "@chakra-ui/react";
import {KeyboardEvent, useContext, useEffect, useRef, useState} from "react";
import {BiChevronDown, BiTrash} from "react-icons/bi";
import BeaconAddressInput from "../../form/BeaconAddressInput";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {useOrganizationLibrary} from "../../area/useOrganizationLibrary";

export type DraftBeaconCreatePayload = {
    type: "Beacon"
    macAddr: string
} | {
    type: "iBeacon"
    uuid: string
    majorId: string
    minorId: string
};

type Props = {
    onCreate: (payload: DraftBeaconCreatePayload) => void;
    onCancel: () => void;
    disabled?: boolean;
};

const HEX_ONLY = /[^0-9A-Fa-f]/g;
const HEX_OR_DASH = /[^0-9A-Fa-f-]/g;

const formatFullUuid = (raw: string): string => {
    const hex = raw.replace(HEX_ONLY, "").toUpperCase();
    if (hex.length !== 32) {
        return hex;
    }
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
};

const DraftBeaconTriggerDetails = ({onCreate, onCancel, disabled}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const area = appContext.getArea();
    const {iBeaconUuids: libraryUuids, addIBeaconUuid} = useOrganizationLibrary(area?.organizationId);

    const [mode, setMode] = useState<"Beacon" | "iBeacon">("iBeacon");
    const [uuid, setUuid] = useState<string>("");
    const createdRef = useRef(false);

    const uuidHex = uuid.replace(HEX_ONLY, "");
    const isUuidComplete = uuidHex.length === 32;

    const enterBlurs = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.currentTarget.blur();
        }
    };

    useEffect(() => {
        if (createdRef.current || mode !== "iBeacon" || !isUuidComplete) {
            return;
        }
        createdRef.current = true;
        const formatted = formatFullUuid(uuidHex);
        addIBeaconUuid(formatted);
        onCreate({
            type: "iBeacon",
            uuid: formatted,
            majorId: "0",
            minorId: "0"
        });
    }, [isUuidComplete, mode, uuidHex, addIBeaconUuid, onCreate]);

    return (
        <>
            <GridItem marginTop={"16px"} colSpan={4}/>
            <GridItem colStart={1}>
                <Text fontWeight={"semibold"} color={"gray.600"}>{mode === "Beacon" ? "Beacon" : "iBeacon"}</Text>
            </GridItem>

            <GridItem colStart={2} data-testid={"draft-beacon-trigger-row"}>
                <Grid gridRowGap={"2"} gridColumnGap={"4"} gridTemplateColumns={"max-content max-content max-content max-content"}>
                    <Box>Type:</Box>
                    <Box gridColumn={"2 / span 3"}>
                        <ButtonGroup size={"xs"} isAttached variant={"outline"}>
                            <Button
                                colorScheme={mode === "iBeacon" ? "blue" : undefined}
                                variant={mode === "iBeacon" ? "solid" : "outline"}
                                onClick={() => setMode("iBeacon")}
                                isDisabled={disabled}
                            >
                                iBeacon
                            </Button>
                            <Button
                                colorScheme={mode === "Beacon" ? "blue" : undefined}
                                variant={mode === "Beacon" ? "solid" : "outline"}
                                onClick={() => setMode("Beacon")}
                                isDisabled={disabled}
                            >
                                Beacon (MAC)
                            </Button>
                        </ButtonGroup>
                    </Box>

                    {mode === "Beacon" ? (
                        <>
                            <Box>Address:</Box>
                            <BeaconAddressInput
                                inputTestId={"draft-beacon-address-input"}
                                beaconAddress={""}
                                index={0}
                                onBeaconAddressChange={(macAddr) => {
                                    if (!macAddr) {
                                        return;
                                    }
                                    onCreate({type: "Beacon", macAddr: macAddr});
                                }}
                            />
                            <Box gridColumn={"3 / span 2"}>
                                <Text fontSize={"sm"} color={"gray.500"}>
                                    Enter a valid MAC to create the trigger.
                                </Text>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box>UUID:</Box>
                            <Box gridColumn={"2 / span 3"}>
                                <InputGroup size={"sm"} width={"320px"}>
                                    <Input
                                        placeholder={"xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
                                        value={uuid}
                                        isInvalid={uuid.length > 0 && !isUuidComplete}
                                        onChange={(e) => setUuid(e.target.value.replace(HEX_OR_DASH, "").toUpperCase().slice(0, 36))}
                                        onKeyDown={enterBlurs}
                                        disabled={disabled}
                                        title={"Full iBeacon UUID (32 hex digits, with or without dashes). Saved automatically when complete; major/minor default to 0."}
                                        paddingRight={libraryUuids.length > 0 ? "32px" : undefined}
                                    />
                                    {libraryUuids.length > 0 ? (
                                        <InputRightElement width={"28px"}>
                                            <Menu>
                                                <MenuButton
                                                    as={IconButton}
                                                    aria-label={"Pick saved UUID"}
                                                    icon={<BiChevronDown/>}
                                                    size={"xs"}
                                                    variant={"ghost"}
                                                    isDisabled={disabled}
                                                />
                                                <MenuList maxHeight={"240px"} overflowY={"auto"} fontSize={"sm"}>
                                                    {libraryUuids.map((libraryUuid) => (
                                                        <MenuItem
                                                            key={libraryUuid}
                                                            fontFamily={"mono"}
                                                            onClick={() => setUuid(libraryUuid)}
                                                        >
                                                            {libraryUuid}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </Menu>
                                        </InputRightElement>
                                    ) : null}
                                </InputGroup>
                            </Box>
                            <Box gridColumn={"1 / span 4"}>
                                <Text fontSize={"sm"} color={"gray.500"}>
                                    Major and minor are shown when the UUID is complete
                                </Text>
                            </Box>
                        </>
                    )}
                </Grid>
            </GridItem>

            <GridItem colStart={4}>
                <IconButton
                    variant={"ghost"}
                    aria-label={"Delete"}
                    icon={<BiTrash/>}
                    onClick={onCancel}
                    disabled={disabled}
                />
            </GridItem>
        </>
    );
};

export default DraftBeaconTriggerDetails;
